import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { apiClient, unwrapApiResult, withAbortSignal } from "@/lib/api-client";

const STALE = 30_000;
const TIMEOUT = 15_000;

export function usePublishedArticles(limit = 10) {
  return useQuery({
    queryKey: ["articles", "published", limit],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "published-articles",
        routeTag: "usePublishedArticles",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase
              .from("articles")
              .select("*")
              .eq("status", "published")
              .order("published_at", { ascending: false })
              .limit(limit),
            abortSignal
          ),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useArticleBySlug(slug: string) {
  return useQuery({
    queryKey: ["articles", "slug", slug],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any | null>({
        label: "article-by-slug",
        routeTag: "useArticleBySlug",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase
              .from("articles")
              .select("*")
              .eq("slug", slug)
              .eq("status", "published")
              .maybeSingle(),
            abortSignal
          ),
      });

      return unwrapApiResult(result);
    },
    enabled: !!slug,
    staleTime: STALE,
    retry: 1,
  });
}

export function useAllArticles() {
  return useQuery({
    queryKey: ["articles", "all"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "all-articles",
        routeTag: "useAllArticles",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("articles").select("*").order("created_at", { ascending: false }),
            abortSignal
          ),
      });

      return unwrapApiResult(result) ?? [];
    },
    staleTime: STALE,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useArticleById(id: string) {
  return useQuery({
    queryKey: ["articles", "id", id],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any | null>({
        label: "article-by-id",
        routeTag: "useArticleById",
        timeoutMs: TIMEOUT,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("articles").select("*").eq("id", id).maybeSingle(),
            abortSignal
          ),
      });

      return unwrapApiResult(result);
    },
    enabled: !!id,
    staleTime: STALE,
    retry: 1,
  });
}
